import SecurityManager from 'modules/security/SecurityManager'

/**
 * Accès au bugtracker « Suggestions & Bugs » — miroir CLIENT des règles du serveur
 * (tasev3-api : src/modules/bugtracker). Sert uniquement à masquer/afficher : le droit
 * réel est appliqué par chaque commande de l'API.
 *
 * Quatre permissions, accès hiérarchique (ADMIN ⊇ STAFF ⊇ CAD) :
 *   - permission CAD `bugtracker` (par faction/grade)  → catégorie CAD
 *   - api.bugtracker.canSeeStaff                       → STAFF (+ CAD)
 *   - api.bugtracker.canSeeAdmin                       → ADMIN (+ STAFF + CAD)
 *   - api.bugtracker.canManage                         → tout + gestion
 */

export const BUGTRACKER_STAFF_PERMISSIONS = [
  'api.bugtracker.canSeeStaff',
  'api.bugtracker.canSeeAdmin',
  'api.bugtracker.canManage',
]

/**
 * La permission CAD est indexée par faction (cad.<job>.bugtracker) : on balaie donc les
 * factions de l'utilisateur au lieu d'un hasPermission("cad.bugtracker") qui ne matcherait
 * jamais rien.
 */
const hasCadBugtrackerPermission = () => {
  const permissions = SecurityManager.getPermissions() || {}
  const cad = permissions.cad || {}
  return Object.keys(cad).some(job => cad[job] && cad[job].bugtracker)
}

/** Peut VOIR le bugtracker (donc : bouton sidebar affiché). */
export const hasBugtrackerAccess = () =>
  SecurityManager.hasPermission(BUGTRACKER_STAFF_PERMISSIONS) || hasCadBugtrackerPermission()

/**
 * L'utilisateur relève-t-il du STAFF (par opposition à un agent CAD) ?
 *
 * Sert aux avertissements de confidentialité sur la catégorie CAD : celle-ci est lue par
 * des agents habilités qui ne sont pas staff (responsables de faction, services publics),
 * ce qu'un membre du staff n'a aucune raison de deviner en écrivant.
 */
export const isBugtrackerStaff = () => SecurityManager.hasPermission(BUGTRACKER_STAFF_PERMISSIONS)

/**
 * Responsable du bugtracker (api.bugtracker.canManage).
 *
 * En plus de la gestion (statut, priorité, édition), il suit TOUT : notifié de chaque
 * nouveau ticket et de chaque réponse, et son compteur de non-lus porte sur l'ensemble
 * des tickets — pas seulement ceux où il est intervenu.
 */
export const isBugtrackerManager = () => SecurityManager.hasPermission('api.bugtracker.canManage')

/**
 * Catégories accessibles — donc aussi celles où l'on peut CRÉER : « si tu as accès, tu as
 * accès ». Miroir de getAccessibleScopes() côté API.
 *
 * ⚠️ Une règle plus étroite avait été essayée (chaque population ne déposait que dans SA
 * catégorie) : elle privait un staff également agent CAD du bouton de création CAD, alors
 * qu'il lit et répond déjà dans cette catégorie. Ne pas la réintroduire.
 */
export const getCreatableScopes = () => {
  const manager = SecurityManager.hasPermission('api.bugtracker.canManage')
  const admin = manager || SecurityManager.hasPermission('api.bugtracker.canSeeAdmin')
  const staff = admin || SecurityManager.hasPermission('api.bugtracker.canSeeStaff')
  const cad = staff || hasCadBugtrackerPermission()

  const scopes = []
  if (cad) scopes.push('CAD')
  if (staff) scopes.push('STAFF')
  if (admin) scopes.push('ADMIN')
  return scopes
}

/**
 * Catégorie proposée par défaut à la création : celle où l'on dépose le plus souvent.
 *
 * ⚠️ Pas simplement la première de getCreatableScopes() : elle rend CAD en tête, si bien
 * qu'un manager (qui peut créer partout) se voyait proposer CAD — la catégorie la plus
 * exposée — alors qu'il écrit presque toujours pour le staff. Un agent CAD, lui, n'a que
 * CAD et le garde.
 */
export const getDefaultCreatableScope = () => {
  const scopes = getCreatableScopes()
  if (isBugtrackerStaff() && scopes.includes('STAFF')) {
    return 'STAFF'
  }
  return scopes[0] || 'CAD'
}
